
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { History } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";
import { Client } from "@/models/Client";
import { useTranslation } from "react-i18next";
import ClientBadges from "./ClientBadges";
import ClientInfo from "./ClientInfo";
import ClientSubscriptions from "./ClientSubscriptions";
import ClientActions from "./ClientActions";

interface ClientCardProps {
  client: Client;
  onEdit?: (client: Client) => void;
  onDelete?: (client: Client) => void;
  onViewUpcomingBookings?: (client: Client) => void;
  onViewSessionHistory?: (client: Client) => void;
  canManage?: boolean;
  onClientClick?: (client: Client) => void;
}

const ClientCard: React.FC<ClientCardProps> = ({
  client,
  onEdit,
  onDelete,
  onViewUpcomingBookings,
  onViewSessionHistory,
  canManage = false,
  onClientClick,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const handleCardClick = async () => {
    // Save scroll position before navigation
    console.log('🖱️ Client card clicked, saving navigation state...');
    onClientClick?.(client);
    
    // Small delay to ensure localStorage write completes
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Clean up URL parameters before navigating to ensure clean back navigation
    const currentUrl = new URL(window.location.href);
    const hasSearchParams = currentUrl.search !== '';
    
    if (hasSearchParams) {
      // Replace current history entry with clean URL (without search params)
      const cleanUrl = '/clients';
      window.history.replaceState(null, '', cleanUrl);
    }
    
    navigate(`/client/${client.id}`);
  };

  const handleHistoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewSessionHistory?.(client);
  };

  return (
    <Card 
      className="h-full cursor-pointer hover:shadow-md transition-shadow dark:hover:shadow-lg dark:border-gray-700 flex flex-col" 
      onClick={handleCardClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-medium truncate greek-text mb-1">
              {client.firstName} {client.lastName}
            </CardTitle>
            <ClientBadges client={client} />
          </div>
          <Button
            onClick={handleHistoryClick}
            size="sm"
            variant="ghost"
            title={t('clients.sessionHistory')}
            className="h-8 w-8 p-0 flex-shrink-0"
          >
            <History className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-3 md:p-4 space-y-3 md:space-y-4 flex-1 flex flex-col">
        <ClientInfo client={client} />
        <div className="flex-1">
          <ClientSubscriptions client={client} maxDisplay={2} />
        </div>
        <ClientActions 
          client={client}
          onEdit={onEdit}
          onDelete={onDelete}
          onViewUpcomingBookings={onViewUpcomingBookings}
          canManage={canManage}
        />
      </CardContent>
    </Card>
  );
};

export default ClientCard;
